import React, { useState } from 'react';
import { millisecondsToMinutes } from 'date-fns';
import { useErrorDialog } from '@ianesthesie-kit/redux';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { TranslatedText, useCountdown } from '@ianesthesie-kit/core';
import { SentryService } from '@ianesthesie-kit/sentry';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  StandaloneOtpInput,
  StandaloneOtpInputProps
} from '@ianesthesie-kit/otp-input';
import { OTP_TTL } from '@/config';

export type LegacyOtpInputProps = Omit<
  StandaloneOtpInputProps,
  'onSubmit' | 'onComplete'
> & {
  onComplete: (otp: string) => Promise<unknown>;
  onResend: () => Promise<unknown>;
  disabled?: boolean;
};

/** @deprecated */
export const LegacyOtpInput = ({
  onComplete,
  onResend,
  disabled,
  ...props
}: LegacyOtpInputProps) => {
  const { showErrorDialog } = useErrorDialog();
  const [resending, setResending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [otp, setOtp] = useState('');
  const {
    countdown,
    isRunning: isCountdownRunning,
    cancel: cancelCountdown,
    restart: restartCountdown
  } = useCountdown(OTP_TTL, {
    intervalMs: 1000
  });

  const resendOtp = () => {
    if (isCountdownRunning) {
      return;
    }
    setResending(true);
    onResend()
      .then(restartCountdown)
      .catch((err) => {
        showErrorDialog(err);
        if (
          err?.isAxiosError &&
          err.response?.status &&
          err.response.status >= 500
        ) {
          SentryService.getInstance()
            .addBreadcrumb({
              level: 'error',
              message: 'Account confirmation failed',
              data: err.response.data as object
            })
            .captureException(err.response.data);
        }
      })
      .finally(() => setResending(false));
  };

  const submitOtp = (payload: string) => {
    if (!payload) {
      return;
    }
    cancelCountdown();
    setSubmitting(true);

    onComplete(payload)
      .catch((err) => {
        setSubmitting(false);
        setOtp('');
        showErrorDialog(err);
        if (
          err?.isAxiosError &&
          err.response?.status &&
          err.response.status >= 500
        ) {
          SentryService.getInstance()
            .addBreadcrumb({
              level: 'error',
              message: 'Account confirmation failed',
              data: err.response.data as object
            })
            .captureException(err.response.data);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <>
      <StandaloneOtpInput
        value={otp}
        containerProps={{
          mt: 2,
          mb: 4
        }}
        onChange={setOtp}
        onComplete={submitOtp}
        disabled={submitting || !!disabled}
        {...props}
      />
      <Stack alignItems="center" gap={1}>
        {isCountdownRunning && (
          <>
            <Typography color="text.secondary" variant="caption">
              <FormattedMessage
                id="app.auth.register.otp.timerCaption"
                values={{
                  minutes: millisecondsToMinutes(OTP_TTL)
                }}
              />
            </Typography>
            <CircularProgress
              size={32}
              thickness={4}
              color="inherit"
              variant="determinate"
              value={(countdown / OTP_TTL) * 100}
              sx={{
                mx: 'auto'
              }}
            />
          </>
        )}
        {!isCountdownRunning && (
          <>
            <Typography color="text.secondary">
              <TranslatedText id="app.auth.register.otp.resendCaption" />
            </Typography>
            <Button
              variant="text"
              onClick={resendOtp}
              disabled={resending}
              startIcon={<ReplayIcon />}
            >
              <TranslatedText id="app.auth.register.otp.resendButton" />
            </Button>
          </>
        )}
      </Stack>
    </>
  );
};
